<template>
  <div data-test="global-filter" class="global-filters d-flex">
    <div data-test="applied-filters-section" class="active-bar mds-text--small-bold">
      {{ $t("filter.appliedFilters") }}
      <mc-tag
        v-if="activeFilter && activeFilter.length"
        appearance="neutral-default"
        withaction="true"
        class="ml-2"
        @dismiss="updateFilter(0, activeFilter[0].label)"
      >
        <span class="mds-text--small-bold mr-1">{{ activeFilter[0].label }}: </span>
        <span class="mds-text--small-normal">{{ activeFilter[0].value }}</span>
      </mc-tag>
      <div v-if="activeFilter.length > 1" class="ml-2">
        <a href="javascript:;" @click="showMore = !showMore">+ {{ activeFilter.length - 1 }} {{ $t("common.more") }}</a>
      </div>
    </div>
    <div class="global-filters__filter-buttons">
      <a
        v-if="activeFilter.length"
        href="javascript:;"
        data-test="clear-filters-button"
        class="mr-3 d-flex-md"
        @click="clearFilters"
      >
        {{ $t("filter.button.clearAll.label") }}
      </a>
      <mc-button
        data-test="saved-filters-button"
        :label="$t('filter.savedFilters')"
        appearance="neutral"
        :class="{ 'active-filter': showSavedFilters }"
        fit="large"
        icon="star"
        @click="toggleSavedFilters"
      />
      <mc-button
        data-test="shipment-filters-button"
        :label="$t('filter.filters')"
        appearance="neutral"
        fit="large"
        :class="{ 'active-filter': showShipmentFilters }"
        :trailingicon="activeFilter && activeFilter.length ? 'check-circle' : ''"
        icon="sliders-horizontal"
        class="d-block-sm"
        @click="toggleShipmentFilters"
      />
      <mc-button
        data-test="shipment-filters-button"
        :label="$t('filter.shipmentFilters')"
        appearance="neutral"
        fit="large"
        class="d-flex-md global-filters__filter-buttons__shipment-filters-button"
        :class="{ 'active-filter': showShipmentFilters }"
        icon="sliders-horizontal"
        @click="toggleShipmentFilters"
      />
    </div>
  </div>
  <div v-if="showMore" class="show-more-filter">
    <div class="filter-tags">
      <mc-tag
        v-for="(filter, index) in showMoreList"
        :key="index"
        appearance="neutral-default"
        withaction="true"
        class="filter-tag"
        @dismiss="updateFilter(index, filter.label)"
      >
        <span class="mds-text--small-bold mr-1">{{ filter.label }}: </span>
        <span class="mds-text--small-normal">{{ filter.value }}</span>
      </mc-tag>
    </div>
  </div>
  <div v-if="showSavedFilters" data-test="saved-filters-section">
    <SavedFilters />
  </div>
  <div v-if="showShipmentFilters" data-test="shipment-filters-section">
    <div class="d-block-md">
      <ShipmentFilters />
    </div>

    <mc-modal
      v-if="isMobile"
      :open="showShipmentFilters"
      :heading="$t('filter.filters')"
      @closed="showShipmentFilters = false"
    >
      <a href="javascript:;" data-test="saved-filters-button" class="mr-3 clear-filter" @click="clearFilters">
        {{ $t("filter.button.clear.label") }}
      </a>
      <div class="mt-3">
        <ShipmentFilters />
      </div>
      <mc-button
        v-if="showShipmentFilters"
        id="done"
        slot="primaryAction"
        data-test="close-button"
        :aria-label="$t('filter.button.apply.label')"
        @click="toggleShipmentFilters"
      >
        {{ $t("common.done") }}
      </mc-button>

      <mc-button
        v-if="showShipmentFilters"
        id="save"
        slot="secondaryAction"
        class="ml-4"
        appearance="neutral"
        :disabled="disableFilterActions"
        data-test="save-button-mobile"
        icon="star"
        :label="$t('filter.button.saveFilter.label')"
        @click="toggleSaveFiltersModal"
      />
    </mc-modal>
    <SaveFilter />
  </div>
  <div v-if="showSavedFilters || showShipmentFilters" class="filter-actions">
    <mc-button
      v-if="showSavedFilters"
      id="close"
      class="close"
      :label="$t('common.close')"
      appearance="neutral"
      data-test="close-button"
      @click="toggleSavedFilters"
    />

    <mc-button
      v-if="showShipmentFilters"
      id="done"
      data-test="close-button"
      :aria-label="$t('filter.button.apply.label')"
      @click="toggleShipmentFilters"
    >
      {{ $t("common.done") }}
    </mc-button>

    <mc-button
      v-if="showShipmentFilters"
      id="save"
      class="ml-4"
      appearance="neutral"
      :disabled="disableFilterActions"
      data-test="save-button"
      :label="$t('filter.button.saveFilter.label')"
      icon="star"
      @click="toggleSaveFiltersModal"
    />
  </div>
</template>
<script>
import { mapActions, mapState } from "pinia";
import { filters } from "@/store/filters";
import SavedFilters from "@/components/filters/SavedFilters.vue";
import ShipmentFilters from "@/components/filters/ShipmentFilters.vue";
import emitter from "tiny-emitter/instance";
import SaveFilter from "@/components/modals/SaveFilter.vue";

export default {
  name: "GlobalFilter",
  components: { SaveFilter, SavedFilters, ShipmentFilters },
  data() {
    return {
      shipmentFilters: [],
      showSavedFilters: false,
      showShipmentFilters: false,
      showMore: false
    };
  },
  computed: {
    ...mapState(filters, ["activeFilter"]),
    showMoreList() {
      return this.activeFilter.slice(1);
    },
    disableFilterActions() {
      if (this.activeFilter && this.activeFilter.length) {
        return false;
      }
      return true;
    },
    isMobile() {
      return window.matchMedia("(max-width: 700px)").matches;
    }
  },
  watch: {
    activeFilter: {
      handler() {
        this.resetTabs();
      },
      deep: true
    }
  },
  mounted() {
    emitter.on("toggle-saved-filters", this.closeSavedFilters);
  },
  methods: {
    ...mapActions(filters, [
      "clearFilters",
      "getVessels",
      "getRoles",
      "updateFromLocation",
      "updateToLocation",
      "updateVessel",
      "updateRole",
      "resetTabs"
    ]),
    toggleSavedFilters() {
      this.showSavedFilters = !this.showSavedFilters;
      this.showShipmentFilters = false;
    },
    closeSavedFilters() {
      this.showSavedFilters = false;
    },
    async toggleShipmentFilters() {
      this.showShipmentFilters = !this.showShipmentFilters;
      this.showSavedFilters = false;
      if (this.showShipmentFilters) {
        await this.getVessels();
        await this.getRoles();
      }
    },
    updateFilter(index, label) {
      if (this.activeFilter?.length) {
        this.showMore = false;
      }
      switch (label) {
        case "From":
          this.updateFromLocation(null);
          break;
        case "To":
          this.updateToLocation(null);
          break;
        case "Vessel":
          this.updateVessel(null);
          break;
        case "Role":
          this.updateRole(null);
          break;
      }
    },
    toggleSaveFiltersModal() {
      emitter.emit("save-filter");
    }
  }
};
</script>
